/**
 * Service Worker management for handling chunk loading and caching
 */

import { tryReloadOnceForChunkError } from './chunk-load-error';

let registration: ServiceWorkerRegistration | null = null;

export class ServiceWorkerManager {
  private static isSupported(): boolean {
    return 'serviceWorker' in navigator;
  }

  /**
   * Register the service worker
   */
  static async register(): Promise<void> {
    if (!this.isSupported()) {
      console.warn('Service Worker not supported');
      return;
    }

    try {
      registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      console.log('Service Worker registered successfully');

      // Listen for service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration?.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available
              this.handleNewVersion();
            }
          });
        }
      });

      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        this.handleServiceWorkerMessage(event);
      });

      // Precache discovered chunks
      this.precacheDiscoveredChunks();

    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }

  /**
   * Handle messages from service worker
   */
  private static handleServiceWorkerMessage(event: MessageEvent): void {
    const { type, url } = event.data;

    switch (type) {
      case 'CHUNK_ERROR':
        console.warn('Chunk loading failed, recovering with reload:', url);
        tryReloadOnceForChunkError();
        break;
      default:
        break;
    }
  }

  /**
   * Handle new service worker version
   */
  private static handleNewVersion(): void {
    // Could show a toast notification here
    console.log('New version available');
    
    // Auto-update after a short delay if no user interaction
    setTimeout(() => {
      this.updateServiceWorker();
    }, 5000);
  }

  /**
   * Update to new service worker version
   */
  static updateServiceWorker(): void {
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  }

  /**
   * Precache chunks that are discovered during runtime
   */
  private static precacheDiscoveredChunks(): void {
    // Get all script tags and extract chunk URLs
    const scriptTags = document.querySelectorAll('script[src]');
    const chunks: string[] = [];

    scriptTags.forEach((script) => {
      const src = (script as HTMLScriptElement).src;
      if (src.includes('/assets/') && (src.endsWith('.js') || src.endsWith('.css'))) {
        chunks.push(src);
      }
    });

    // Also check link tags for CSS
    const linkTags = document.querySelectorAll('link[href]');
    linkTags.forEach((link) => {
      const href = (link as HTMLLinkElement).href;
      if (href.includes('/assets/') && href.endsWith('.css')) {
        chunks.push(href);
      }
    });

    if (chunks.length > 0 && registration?.active) {
      registration.active.postMessage({
        type: 'PRECACHE_CHUNKS',
        chunks: chunks
      });
    }
  }

  /**
   * Check if service worker is active
   */
  static isActive(): boolean {
    return registration?.active !== null;
  }

  /**
   * Unregister service worker (for development/debugging)
   */
  static async unregister(): Promise<void> {
    if (registration) {
      await registration.unregister();
      registration = null;
      console.log('Service Worker unregistered');
    }
  }
}